// Copyright © 2017-2020  SDG Systems, LLC. All rights reserved.
// This file may not be used in any form without the express written permission of SDG Systems, LLC.

<template>
  <v-container fluid grid-list-md>
    <v-row wrap>
      <v-col cols="12" justify-center>
        <v-card>
          <v-progress-linear :indeterminate="loading" style="margin-top: 0px;"></v-progress-linear>
          <v-card-text>
            <v-row wrap>
              <v-col cols="12" md="3">
                <date-time-view
                v-model="dateStart"
                label="Start Date"
                ></date-time-view>
              </v-col>
              <v-col cols="12" md="3">
                <date-time-view
                v-model="dateEnd"
                label="End Date"
                ></date-time-view>
              </v-col>
              <v-col cols="12" md="6">
                <v-btn dark color="blue darken-1" class="mx-1" @click="resetDate()">Today</v-btn>
                <v-btn dark color="blue darken-2" class="mx-1" @click="resetWeek()">Last 7 Days</v-btn>
                <v-btn dark color="blue darken-3" class="mx-1" @click="resetMonth()">Last 30 Days</v-btn>
                <v-btn v-if="showExpButton" class="mx-1" @click="showHideAll()">{{ showHideText }}</v-btn>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon @click="getEvents()"><v-icon>refresh</v-icon></v-btn>
                  </template>
                  <span>Refresh Data</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon @click="showGraph = !showGraph">
                      <v-icon v-if="showGraph">view_agenda</v-icon>
                      <v-icon v-else>equalizer</v-icon>
                    </v-btn>
                  </template>
                  <span v-if="showGraph">Show Data</span>
                  <span v-else>Show Graph</span>
                </v-tooltip>
                {{ events.length }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row wrap>
      <v-col cols="12">
        <v-card>
          <div style="padding: 12px; padding-left: 24px; padding-right: 24px; ">
            <v-row wrap>
              <v-col cols="12" md="6" class="font-weight-bold">
                Total Tags: {{ data.length }}
              </v-col>
              <v-col cols="12" md="6" class="font-weight-bold">
                Report Total Hours: {{ durationFormat(totalHours) }}
              </v-col>
              <v-col cols="12" md="6" class="font-weight-bold">
                Start Date: {{ formatTime(dateStart) }}
              </v-col>
              <v-col cols="12" md="6" class="font-weight-bold">
                End Date: {{ formatTime(dateEnd) }}
              </v-col>
            </v-row>
          </div>
        </v-card>
      </v-col>
        <v-expansion-panels v-model="panel" multiple>
          <v-expansion-panel v-for="(thing,i) in data" :key="i" expand style="margin-left:10px">
            <v-expansion-panel-header style="border-top: 1px solid rgba(0, 0, 0, 0.92);">
              <v-row wrap>
                <v-col cols="12" md="6" class="font-weight-bold">
                  {{ thing.details.common.name }} - {{ getThingTags(thing.details) }}
                </v-col>
                <v-col cols="12" md="6" class="font-weight-bold">
                  Total Hours: {{ durationFormat(thing.duration) }}
                </v-col>
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-card>
                <v-card-text>
                  <template v-for="(event, j) in thing.events">
                    <v-row wrap :key="thing.id + j" style="border-top: 1px solid rgba(0, 0, 0, 0.82);" dense>
                      <v-col cols="12" md="3" class="font-weight-bold">
                        {{ getZoneName(event.zone)}}
                      </v-col>
                      <v-col cols="12" md="3" >
                        Duration: {{ durationFormat(event.timeDuration) }}
                      </v-col>
                      <v-col cols="12" md="3" >
                        Entry: {{ formatTime(event.timeStart) }}
                      </v-col>
                      <v-col cols="12" md="3" >
                        Exit: {{ formatTime(event.timeEnd) }}
                      </v-col>
                    </v-row>
                  </template>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      <v-col cols="12" v-if="showGraph">
        <v-card>
          <v-card-text>
            <chart-bar :chart-data="chartData" :options="chartOptions" style="height: 55vh;"></chart-bar>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import ApiRequests from '@/api/ApiRequests'
import ChartBar from '@/components/ChartBar.js'
import DateTimeView from '@/components/DateTimeView'

import {LookupMixin, TimeMixin} from '@/shared/mixins'

export default {
  components: {
    DateTimeView,
    ChartBar
  },

  mixins: [LookupMixin, TimeMixin],

  data () {
    return {
      chartData: {},
      chartOptions: {
        maintainAspectRatio: false
      },
      dateStart: (moment().startOf('day')),
      dateEnd: (moment().add(1, 'days').startOf('day')),
      data: [],
      events: [],
      loading: false,
      panel: [],
      showExpButton: false,
      showGraph: false,
      things: [],
      totalHours: 0,
      zones: []
    }
  },

  created () {
    Promise.all([
      ApiRequests.getThings().then(r => {
        this.things = r.data
        return r
      }),
      ApiRequests.getZones(this.selectedSite._id).then(r => {
        this.zones = r.data
        return r
      })
    ]).then(r => {
      this.getEvents()
    })
  },

  computed: {
    ...mapGetters([
      'selectedSite',
      'user'
    ]),

    showHideText: function () {
      if (this.panel.length > 0) {
        return 'Collapse All'
      }
      return 'Expand All'
    }
  },

  watch: {
    dateEnd: function () {
      this.getEvents()
    },

    dateStart: function () {
      this.getEvents()
    },

    selectedSite: function () {
      this.getEvents()
    },

    data: function () {
      if (this.data.length > 0) {
        this.showExpButton = true
        return
      }
      this.showExpButton = false
    }
  },

  methods: {
    compare: function (a, b) {
      var aName = a.details.common.name.toUpperCase()
      var bName = b.details.common.name.toUpperCase()
      if (aName < bName) {
        return -1
      } else if (bName < aName) {
        return 1
      }
      return 0
    },

    durationFormat: function (seconds) {
      var duration = moment.duration(seconds / 1000000)
      return Math.floor(duration.as('hours')) + ':' + this.pad(duration.minutes()) + ':' + this.pad(duration.seconds())
    },

    pad: function (num) {
      if (num < 10) {
        return '0' + num
      }
      return num
    },

    fillData () {
      var dataAverage = []
      var dataTotal = []
      var labels = []

      for (var i in this.zones) {
        var d = this.zones[i]
        dataAverage.push({
          y: d.average,
          x: i
        })
        dataTotal.push({
          y: d.total,
          x: i
        })
        labels.push(d.name)
      }

      this.chartOptions = {}
      this.chartOptions = {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {}
          ],
          yAxes: [
            {
              id: 'A',
              position: 'left',
              scaleLabel: {
                display: true,
                labelString: 'Total Time'
              },
              ticks: {
                callback: (value, index, values) => {
                  return this.durationFormat(value)
                },
                suggestedMin: 0
              }
            },
            {
              id: 'B',
              position: 'right',
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                labelString: 'Average Time'
              },
              ticks: {
                callback: (value, index, values) => {
                  return this.durationFormat(value)
                },
                suggestedMin: 0
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              return this.durationFormat(tooltipItem.yLabel)
            },
            title: (tooltipItem, data) => {
              return tooltipItem[0].label + ' - ' + data.datasets[tooltipItem[0].datasetIndex].label
            }
          }
        }
      }
      this.chartData = {
        labels: labels,
        datasets: [
          {
            label: 'Total Time (h:mm:ss)',
            yAxisID: 'A',
            borderColor: 'rgba(118, 149, 185, 1.0)',
            borderWidth: 2,
            backgroundColor: 'rgba(118, 149, 185, 0.5)',
            data: dataTotal
          },
          {
            label: 'Average Time (h:mm:ss)',
            yAxisID: 'B',
            borderColor: 'rgba(226, 184, 104, 1.0)',
            borderWidth: 2,
            backgroundColor: 'rgba(226, 184, 104, 0.5)',
            data: dataAverage
          }
        ]
      }
    },

    getEvents: function () {
      this.loading = true
      this.panel = []

      ApiRequests.getZoneDurations(this.selectedSite._id,
      moment(this.dateStart).format(),
      moment(this.dateEnd).format(),
      10000).then(r => {
        this.events = r.data

        this.getThingData()
        this.getZoneTimes()
        this.loading = false
      })
    },

    getThingData: function () {
      this.totalHours = 0
      var list = []
      for (var i = 0; i < this.events.length; i++) {
        var event = this.events[i]

        var index = list.findIndex(e => e.id === event.thing)
        if (index < 0) {
          var o = {}
          o.id = event.thing
          o.details = this.getThing(event.thing)
          o.duration = 0
          o.events = []

          if (o.details) {
            list.push(o)
          }
          index = list.length - 1
        }
        list[index].events.push(event)
        list[index].duration = list[index].duration + event.timeDuration
        this.totalHours = this.totalHours + event.timeDuration
      }

      list.sort(this.compare)
      this.data = list
    },

    getZoneTimes: function () {
      for (var i = 0; i < this.zones.length; i++) {
        var zone = this.zones[i]
        var t = this.events.filter(t => t.zone === zone._id)
        zone.total = t.reduce((psum, e) => psum + e.timeDuration, 0)
        zone.length = t.reduce((psum, e) => {
          if (e.timeDuration > 0) {
            return psum + 1
          } else {
            return psum
          }
        }, 0)
        zone.average = zone.total / zone.length
        this.$set(this.zones, i, zone)
      }
      this.fillData()
    },

    resetDate: function () {
      this.dateStart = moment().startOf('day')
      this.dateEnd = moment().add(1, 'days').startOf('day')
    },

    resetWeek: function () {
      this.dateStart = moment().startOf('day').subtract(6, 'days').format()
      this.dateEnd = moment().add(1, 'days').startOf('day')
    },

    resetMonth: function () {
      this.dateStart = moment().startOf('day').subtract(29, 'days').format()
      this.dateEnd = moment().add(1, 'days').startOf('day')
    },

    showHideAll: function () {
      if (this.panel.length > 0) {
        this.panel = []
        return
      }
      this.panel = Array.apply(null, {length: this.data.length}).map(Number.call, Number)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ReportZoneDurations.vue